/**
 * Alert priority and severity utilities.
 *
 * This is the single source of truth for alert priority colour and ordering,
 * shared by the population-level Requires attention table, the participant
 * Requires attention tab (KAN-2355), and the profile sidebar card (KAN-2384).
 * The three surfaces can never disagree about priority colours or order.
 */

import { TableTemplateTag } from "../models/components/table/table-template.model";
import { TagSeverityType } from "../models/components/tag.model";

export const ALERT_SEVERITY_ORDER: Record<string, number> = {
	CRITICAL: 0,
	HIGH: 1,
	MEDIUM: 2,
	LOW: 3,
};

/**
 * Returns the numeric rank for a severity level.
 * Lower numbers sort first. Unknown/missing severities sort last.
 */
export const alertSeverityRank = (severity?: string | null): number => {
	if (!severity || !(severity in ALERT_SEVERITY_ORDER)) {
		return Number.MAX_SAFE_INTEGER;
	}
	return ALERT_SEVERITY_ORDER[severity];
};

/**
 * Maps alert severity to PrimeNG TagSeverityType for visual styling.
 */
export const alertSeverityTagSeverity = (severity?: string | null): TagSeverityType => {
	if (severity === "LOW") return "success";
	if (severity === "MEDIUM") return "warning";
	return "danger";
};

/**
 * Builds a TableTemplateTag for alert priority display.
 */
export const buildAlertPriorityTag = (severity?: string | null): TableTemplateTag => ({
	severity: alertSeverityTagSeverity(severity),
	value: `ENUMS.ALERT_SEVERITY.${severity}`,
	rounded: true,
	uppercase: true,
});

/**
 * Sorts alerts by priority in ascending order (CRITICAL first, LOW last).
 * Returns a new array without mutating the input.
 * Array.prototype.sort is stable in modern JS, so equal-priority items
 * preserve their arrival order (caller-supplied newest-first).
 */
export const sortAlertsByPriority = <T extends { severity?: string }>(alerts: T[]): T[] => {
	const copy = [...alerts];
	copy.sort((a, b) => alertSeverityRank(a.severity) - alertSeverityRank(b.severity));
	return copy;
};
